<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCallControl } from 'dashboard/composables/useCallControl';

const { t } = useI18n();

const {
  isConnected,
  isConnecting,
  currentCall,
  isMuted,
  isOnHold,
  activeCalls,
  error,
  hasActiveCall,
  answerCall,
  hangupCall,
  toggleMute,
  toggleHold,
} = useCallControl();

// Estado do widget
const isVisible = ref(true);
const isMinimized = ref(false);
const isExpanded = ref(false);

// Posição do widget (draggable)
const position = ref({
  x: Math.max(0, window.innerWidth - 380),
  y: 100,
});

// Estado do drag
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

// Computados
const callerDisplayName = computed(() => {
  if (!currentCall.value) return '';
  const { callerName, callerNumber } = currentCall.value;
  return callerName !== 'Desconhecido' ? callerName : callerNumber;
});

const inboxDisplay = computed(() => {
  if (!currentCall.value) return '';
  return currentCall.value.inboxName;
});

const callDuration = computed(() => {
  if (!currentCall.value || !currentCall.value.answeredAt) return '00:00';
  const seconds = Math.floor((Date.now() - currentCall.value.answeredAt) / 1000);
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = (seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
});

const callStatus = computed(() => {
  if (!currentCall.value) return '';

  const statusMap = {
    incoming: t('CALL_CONTROL.INCOMING'),
    answered: t('CALL_CONTROL.IN_PROGRESS'),
    held: t('CALL_CONTROL.ON_HOLD'),
    ringing: t('CALL_CONTROL.RINGING'),
  };

  return statusMap[currentCall.value.status] || currentCall.value.status;
});

const hasIncomingCalls = computed(() => {
  return activeCalls.value.some(call => call.status === 'incoming');
});

// Funções de Drag
const startDrag = (event) => {
  if (event.target.closest('button')) return; // Não arrastar se clicou num botão

  isDragging.value = true;
  dragOffset.value = {
    x: event.clientX - position.value.x,
    y: event.clientY - position.value.y,
  };

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = (event) => {
  if (!isDragging.value) return;

  event.preventDefault();

  const newX = event.clientX - dragOffset.value.x;
  const newY = event.clientY - dragOffset.value.y;

  // Limitar à tela - sem delay (100% da tela)
  const widgetWidth = isMinimized.value ? 200 : 320;
  const widgetHeight = isMinimized.value ? 50 : 200;

  position.value = {
    x: Math.max(0, Math.min(newX, window.innerWidth - widgetWidth)),
    y: Math.max(0, Math.min(newY, window.innerHeight - widgetHeight)),
  };
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

// Toggle estados
const toggleMinimize = () => {
  isMinimized.value = !isMinimized.value;
  if (isMinimized.value) isExpanded.value = false;
};

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
  if (isExpanded.value) isMinimized.value = false;
};

// Limpar event listeners
onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
});
</script>

<template>
  <Teleport to="body">
    <Transition name="widget-fade">
      <div
        v-if="isVisible"
        class="call-control-widget"
        :class="{
          minimized: isMinimized,
          expanded: isExpanded,
          dragging: isDragging,
          connected: isConnected,
        }"
        :style="{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }"
      >
        <!-- Header do Widget (Draggable) -->
        <div
          class="widget-header"
          @mousedown="startDrag"
        >
          <div class="header-content">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="phone-icon"
              :class="{
                connected: isConnected,
                disconnected: !isConnected,
              }"
            >
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>

            <span v-if="!isMinimized" class="header-title">
              {{ hasActiveCall ? callerDisplayName : (isConnected ? 'Chamadas' : 'Desconectado') }}
            </span>

            <!-- Badges -->
            <div v-if="!isMinimized && hasIncomingCalls" class="incoming-badge">
              {{ t('CALL_CONTROL.INCOMING') }}
            </div>
          </div>

          <!-- Controles do Widget -->
          <div class="widget-controls">
            <button
              v-if="!isMinimized && hasActiveCall"
              class="control-btn"
              @click="toggleExpand"
              :title="isExpanded ? 'Recolher' : 'Expandir'"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline v-if="!isExpanded" points="13 7 18 12 13 17"/>
                <polyline v-else points="11 17 6 12 11 7"/>
              </svg>
            </button>

            <button
              class="control-btn"
              @click="toggleMinimize"
              :title="isMinimized ? 'Expandir' : 'Minimizar'"
            >
              <svg
                v-if="!isMinimized"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="13 7 18 12 13 17"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Conteúdo do Widget -->
        <Transition name="widget-content">
          <div v-if="!isMinimized" class="widget-content">
            <!-- Chamada Ativa -->
            <div v-if="hasActiveCall" class="active-call">
              <!-- Informações da Chamada -->
              <div v-if="isExpanded" class="call-info">
                <div class="call-row">
                  <span class="label">Nome:</span>
                  <span class="value">{{ callerDisplayName }}</span>
                </div>
                <div class="call-row">
                  <span class="label">Número:</span>
                  <span class="value">{{ currentCall.callerNumber }}</span>
                </div>
                <div class="call-row">
                  <span class="label">Unidade:</span>
                  <span class="value">{{ inboxDisplay }}</span>
                </div>
                <div class="call-row">
                  <span class="label">Status:</span>
                  <span class="value status-badge">{{ callStatus }}</span>
                </div>
                <div v-if="currentCall.answeredAt" class="call-row timer">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12 6 12 12 16 14"/>
                  </svg>
                  <span>{{ callDuration }}</span>
                </div>
              </div>

              <!-- Controles da Chamada -->
              <div class="call-controls">
                <!-- Atender (só incoming) -->
                <button
                  v-if="currentCall.status === 'incoming'"
                  class="btn-answer"
                  @click="answerCall(currentCall.id)"
                  :title="t('CALL_CONTROL.ANSWER')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </button>

                <!-- Desligar -->
                <button
                  class="btn-hangup"
                  @click="hangupCall(currentCall.id)"
                  :title="t('CALL_CONTROL.HANGUP')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M23 17l-5-3v-4l5-3v10zM1 17l5-3v-4l-5-3v10zM10.5 17l5-3v-4l-5-3v10z"/>
                  </svg>
                </button>

                <!-- Mute (só após atender) -->
                <button
                  v-if="currentCall.status !== 'incoming'"
                  class="btn-control"
                  :class="{ active: isMuted }"
                  @click="toggleMute()"
                  :title="isMuted ? t('CALL_CONTROL.UNMUTE') : t('CALL_CONTROL.MUTE')"
                >
                  <svg v-if="!isMuted" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="1" y1="1" x2="23" y2="23"/>
                    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
                    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                  </svg>
                </button>

                <!-- Hold (só após atender) -->
                <button
                  v-if="currentCall.status !== 'incoming'"
                  class="btn-control"
                  :class="{ active: isOnHold }"
                  @click="toggleHold()"
                  :title="isOnHold ? t('CALL_CONTROL.UNHOLD') : t('CALL_CONTROL.HOLD')"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="10" y1="15" x2="10" y2="9"/>
                    <line x1="14" y1="15" x2="14" y2="9"/>
                  </svg>
                </button>

                <!-- Timer no modo compacto -->
                <div
                  v-if="!isExpanded && currentCall.answeredAt"
                  class="quick-timer"
                >
                  {{ callDuration }}
                </div>
              </div>
            </div>

            <!-- Controles Sempre Visíveis -->
            <div v-else class="call-controls">
              <button
                class="btn-control disabled"
                disabled
                :title="isConnected ? 'Aguardando chamada...' : 'Conecte-se para atender'"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
              </button>
              <button
                class="btn-control disabled"
                disabled
                title="Sem chamada ativa"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              </button>
              <button
                class="btn-control disabled"
                disabled
                title="Sem chamada ativa"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="10" y1="15" x2="10" y2="9"/>
                  <line x1="14" y1="15" x2="14" y2="9"/>
                </svg>
              </button>
            </div>

            <!-- Erro -->
            <div v-if="error" class="error-message">
              {{ error }}
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.call-control-widget {
  position: fixed;
  z-index: 9999;
  background: var(--background, #ffffff);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border, #e5e7eb);
  min-width: 280px;
  max-width: 400px;
  backdrop-filter: blur(10px);
  transition: opacity 0.3s ease, transform 0.2s ease, min-width 0.2s ease, max-width 0.2s ease;
}

.call-control-widget.minimized {
  min-width: auto;
  max-width: none;
}

.call-control-widget.expanded {
  min-width: 400px;
}

.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #1D1E24;
  border-radius: 12px 12px 0 0;
  cursor: move;
  user-select: none;
  color: white;
}

.call-control-widget.minimized .widget-header {
  border-radius: 12px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
}

.phone-icon {
  flex-shrink: 0;
  animation: pulse-ring 2s infinite;
}

.phone-icon.connected {
  color: #22c55e;
  filter: drop-shadow(0 0 4px rgba(34, 197, 94, 0.8));
}

.phone-icon.disconnected {
  color: #ef4444;
  animation: none;
  opacity: 0.7;
}

@keyframes pulse-ring {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.header-title {
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.incoming-badge {
  padding: 0.25rem 0.5rem;
  background: #22c55e;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 600;
  animation: blink 1s infinite;
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.widget-controls {
  display: flex;
  gap: 0.25rem;
}

.control-btn {
  padding: 0.25rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.widget-content {
  padding: 1rem;
}

.widget-content-enter-active,
.widget-content-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.widget-content-enter-from,
.widget-content-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.widget-content-enter-to,
.widget-content-leave-from {
  opacity: 1;
  max-height: 500px;
}

.active-call {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.call-info {
  padding: 0.75rem;
  background: var(--surface, #f9fafb);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.call-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
}

.call-row .label {
  color: var(--muted-foreground, #6b7280);
  font-weight: 500;
}

.call-row .value {
  color: var(--foreground, #111827);
  font-weight: 600;
}

.call-row.timer {
  padding-top: 0.5rem;
  border-top: 1px solid var(--border, #e5e7eb);
  color: #667eea;
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.625rem;
  background: #dbeafe;
  color: #1e40af;
}

.call-controls {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(48px, 1fr));
  gap: 0.5rem;
}

.call-controls button {
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0.75rem;
}

.btn-answer {
  background: #22c55e;
  color: white;
}

.btn-answer:hover {
  background: #16a34a;
  transform: scale(1.05);
}

.btn-hangup {
  background: #ef4444;
  color: white;
}

.btn-hangup:hover {
  background: #dc2626;
  transform: scale(1.05);
}

.btn-control {
  background: var(--muted, #f3f4f6);
  color: var(--foreground, #111827);
}

.btn-control:hover {
  background: var(--muted-foreground, #d1d5db);
}

.btn-control.active {
  background: #667eea;
  color: white;
}

.btn-control.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-control.disabled:hover {
  background: var(--muted, #f3f4f6);
  transform: none;
}

.call-control-widget:not(.connected) .call-controls button {
  cursor: not-allowed;
}

.call-control-widget.connected .call-controls button:not(.disabled) {
  cursor: pointer;
}

.quick-timer {
  grid-column: 1 / -1;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #667eea;
  font-variant-numeric: tabular-nums;
  padding: 0.5rem;
  background: var(--surface, #f9fafb);
  border-radius: 8px;
}

.error-message {
  padding: 0.75rem;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 8px;
  font-size: 0.75rem;
  text-align: center;
}

.widget-fade-enter-active,
.widget-fade-leave-active {
  transition: opacity 0.3s ease;
}

.widget-fade-enter-from,
.widget-fade-leave-to {
  opacity: 0;
}

.call-control-widget.dragging {
  cursor: grabbing;
}

.call-control-widget.dragging .widget-header {
  cursor: grabbing;
}
</style>
